import { useEffect, useState } from 'react';
import {
    IconBrandFacebook, IconCheck, IconClockHour4, IconLoader2, IconShare3, IconStar, IconVideo, IconX,
} from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import contestService from '../../services/contestService';
import { notifications } from '../../lib/notifications';
import { sanitizeUrl } from '../../utils/sanitize';
import { CONTEST_SHARE_POSTS, SHARE_HASHTAG } from '../../config/contestSharePosts';
import { FB_PAGE_URL } from '../../config/fbPromoPosts';
import { CHROME_EXTENSION_URL, CONTEST_APPS, FIREFOX_ADDON_URL, TIKTOK_PROFILE_URL, YOUTUBE_SUBSCRIBE_URL } from '../../config/contestSite';
import { CONTEST_TASK_VIDEOS } from '../../config/contestVideos';
import VideoHelpButton from '../common/VideoHelpButton';
import { BRAND_MARKS } from '../../pages/user/tools/image/qr-code/qrBrandMarks';
import { offeredTaskKeys, reviewTasksOn } from '../../utils/contestTasks';
import { BADGE_DAILY, BADGE_ONCE, CARD, CHIP, useNumFmt } from './contestUi';
import ContestQuizRow from './ContestQuiz';

// Bonus tasks: share (once a DAY) / Facebook follow / YouTube subscribe / TikTok follow / install each of the 3 mobile apps /
// Facebook review / Play Store review / video review.
// The owner taps "Claim" (optionally pasting a proof link) and the contest team awards the points by hand. Two exceptions:
//   • install — like a follow, ONE tap opens Google Play AND sends the claim. The backend awards it by itself as soon as the
//     account has logged in to that app (right away if it already has, otherwise when the owner comes back to this page).
//   • Play Store review — only ONE app's review is offered at a time, and only during ONE random hour a day (data.appTasks.review:
//     decided per user by the backend, absent the rest of the day) so reviews do not hit the store in a burst. The button opens the
//     store and the proof box.
// Everything except share can be claimed once per contest. A task whose reward the admin set to 0 is switched off and not
// listed. Facebook follow, YouTube subscribe and TikTok follow share ONE reward field in the admin (contestPointsFbFollow);
// the 3 installs share contestPointsAppInstall and the 3 app reviews share contestPointsAppReview.

// Each task's icon tile. Brand tasks show the brand's ORIGINAL logo in its own colors on a neutral tile; the generic icons
// (share, video) use the SohozKaj orange, and the Facebook-review star the Facebook blue (hexes as in the QR logo marks).
const TONE = {
    logo: 'bg-zinc-100 dark:bg-zinc-800',
    facebook: 'bg-[#0866FF]/10 text-[#0866FF] dark:bg-[#0866FF]/20',
    sohozkaj: 'bg-primary/10 text-primary dark:bg-primary/20',
};

// Original brand logos. Facebook / YouTube / TikTok use the official marks the QR logo presets already ship (Simple Icons,
// 24×24 paths); they get a little more room than the stroke icons (22px vs 18px) because they are solid shapes.
const LOGO_PX = 4;
const TIKTOK_CYAN = '#25F4EE'; // the two offset layers of the real TikTok logo
const TIKTOK_RED = '#FE2C55';
// Chrome and Firefox in their brand colours (drawn here as simple SVG marks).
function ChromeLogo({ size = 18 }) {
    return (
        <svg width={size + LOGO_PX} height={size + LOGO_PX} viewBox="0 0 48 48" className="flex-none" aria-hidden="true">
            <path d="M6.68 14A20 20 0 0 1 41.32 14L30.93 20A8 8 0 0 0 17.07 20Z" fill="#EA4335" />
            <path d="M41.32 14A20 20 0 0 1 24 44L24 32A8 8 0 0 0 30.93 20Z" fill="#FBBC05" />
            <path d="M24 44A20 20 0 0 1 6.68 14L17.07 20A8 8 0 0 0 24 32Z" fill="#34A853" />
            <circle cx="24" cy="24" r="8" fill="#fff" />
            <circle cx="24" cy="24" r="6" fill="#4285F4" />
        </svg>
    );
}
function FirefoxLogo({ size = 18 }) {
    return (
        <svg width={size + LOGO_PX} height={size + LOGO_PX} viewBox="0 0 48 48" className="flex-none" aria-hidden="true">
            <defs>
                <linearGradient id="ffFlame" x1="38" y1="6" x2="10" y2="42" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#FFE226" />
                    <stop offset=".35" stopColor="#FF9500" />
                    <stop offset=".7" stopColor="#FF3647" />
                    <stop offset="1" stopColor="#E31587" />
                </linearGradient>
                <linearGradient id="ffGlobe" x1="20" y1="14" x2="34" y2="40" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#9059FF" />
                    <stop offset="1" stopColor="#4B1FA8" />
                </linearGradient>
            </defs>
            <path d="M42.6 15.3C41.4 12.4 39.3 9.9 37 8.5c1.4 2.8 2.1 5.6 2.1 7.6-2.5-6.3-6.8-8.8-10.3-14.3-.2-.3-.3-.5-.5-.9-.2-.3-.3-.6-.4-1 0 0-.1 0-.1.1-5.6 3.3-7.5 9.4-7.7 12.4-2.3.2-4.5 1-6.3 2.4-.2-.2-.4-.3-.6-.5-.5-1.8-.5-3.8-.4-5.5-2.200 1.200-4 3-5.400 5.100-1.800 2.600-2.600 5.800-2.900 8.900v.3c0 .500-.1 1-.1 1.500 0 11.500 9.300 20.800 20.800 20.800 10.300 0 18.900-7.500 20.500-17.300.1-.4.1-.9.200-1.300.500-4.500-.5-9.200-1.800-12.300z" fill="url(#ffFlame)" />
            <circle cx="25" cy="27" r="13" fill="url(#ffGlobe)" />
            <path d="M16 20c3-4 9-5.500 14-3-3 .2-6 1.800-7.500 4.500-1.500 2.800-1.300 6.500.8 9" stroke="#C9B3FF" strokeOpacity=".5" strokeWidth="1.600" strokeLinecap="round" fill="none" />
        </svg>
    );
}
function FacebookLogo({ size = 18 }) {
    // The "f" is a cut-out in the mark → a white disc behind it makes the f white on any tile, light or dark.
    return (
        <svg width={size + LOGO_PX} height={size + LOGO_PX} viewBox="0 0 24 24" className="flex-none" aria-hidden="true">
            <circle cx="12" cy="12" r="11.9" fill="#fff" />
            <path d={BRAND_MARKS.facebook.path} fill={BRAND_MARKS.facebook.hex} />
        </svg>
    );
}
function YoutubeLogo({ size = 18 }) {
    // The play triangle is a cut-out too → fill it white so it stays white on a dark tile.
    return (
        <svg width={size + LOGO_PX} height={size + LOGO_PX} viewBox="0 0 24 24" className="flex-none" aria-hidden="true">
            <path d={BRAND_MARKS.youtube.path} fill={BRAND_MARKS.youtube.hex} />
            <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#fff" />
        </svg>
    );
}
function TiktokLogo({ size = 18 }) {
    // Cyan and red copies, nudged apart, sit under the note — black on a light tile, white on a dark one.
    const d = BRAND_MARKS.tiktok.path;
    return (
        <svg width={size + LOGO_PX} height={size + LOGO_PX} viewBox="0 0 24 24" className="flex-none text-black dark:text-white" aria-hidden="true">
            <path d={d} fill={TIKTOK_CYAN} transform="translate(-0.8 -0.8)" />
            <path d={d} fill={TIKTOK_RED} transform="translate(0.8 0.8)" />
            <path d={d} fill="currentColor" />
        </svg>
    );
}
// The Google Play logo (same file the sidebar uses). Its triangle sits in a padded box, so it gets a little more room than the stroke icons.
function GooglePlayLogo({ size = 18 }) {
    return <img src="/images/google-play.svg" alt="" width={size + 3} height={size + 3} className="flex-none" draggable={false} />;
}

// How each task looks. WHICH of them are listed (and their order) is decided by offeredTaskKeys (utils/contestTasks) — the same rule the
// standing card and the summary card use for their totals.
const TASKS = {
    share: { Icon: IconShare3, tone: TONE.sohozkaj },
    fb_follow: { Icon: FacebookLogo, tone: TONE.logo },
    yt_subscribe: { Icon: YoutubeLogo, tone: TONE.logo },
    tiktok_follow: { Icon: TiktokLogo, tone: TONE.logo },
    fb_review: { Icon: IconStar, tone: TONE.facebook },
    ext_chrome: { Icon: ChromeLogo, tone: TONE.logo, kind: 'ext', url: CHROME_EXTENSION_URL },
    ext_firefox: { Icon: FirefoxLogo, tone: TONE.logo, kind: 'ext', url: FIREFOX_ADDON_URL },
    video_review: { Icon: IconVideo, tone: TONE.sohozkaj },
    ...Object.fromEntries(CONTEST_APPS.flatMap((app) => [
        [`install_${app.key}`, { Icon: GooglePlayLogo, tone: TONE.logo, kind: 'install', app }],
        [`review_${app.key}`, { Icon: GooglePlayLogo, tone: TONE.logo, kind: 'review', app }],
    ])),
};
const MAX_URL = 500;
// Tasks claimed with a single tap — there is nothing to paste (following / subscribing leaves no link to show; an install is checked by the backend).
const NO_PROOF = new Set(['fb_follow', 'yt_subscribe', 'tiktok_follow']);
const isNoProof = (activity) => NO_PROOF.has(activity) || activity.startsWith('install_');
// Tasks that also send the owner somewhere as they claim — following / subscribing means being on the page.
const OPEN_ON_CLAIM = { fb_follow: FB_PAGE_URL, yt_subscribe: YOUTUBE_SUBSCRIBE_URL, tiktok_follow: TIKTOK_PROFILE_URL };
const fbShareUrl = (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&hashtag=${encodeURIComponent(SHARE_HASHTAG)}`;
// Opened inside the tap itself, so the browser never treats it as a blocked popup.
const openLink = (url) => window.open(url, '_blank', 'noopener,noreferrer');

const INPUT = 'h-11 w-full min-w-0 flex-1 rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100';

export default function ContestBonusTasks({ data, onChanged }) {
    const { t, currentLanguage } = useTranslation();
    const fmt = useNumFmt();
    const isBn = currentLanguage === 'bn';

    const [openKey, setOpenKey] = useState(null); // task whose claim form is open
    const [proof, setProof] = useState('');
    const [proofBad, setProofBad] = useState(false); // pasted link was rejected
    const [postId, setPostId] = useState(null);
    const [busyKey, setBusyKey] = useState(null); // task being submitted

    const { rules } = data;
    const claimPoints = rules.claimPoints || null; // reward of each task — fixed in the backend (config/contestTasks.js), not typed here
    const dailyKeys = rules.dailyActivities || ['share']; // tasks that can be claimed every day (the rest: once per contest)
    // The titles come from the API too (rules.tasks, same file as the points) so this page and the public contest site read the
    // same; the translations below are only the fallback for a backend that does not send them yet.
    const taskInfo = Object.fromEntries((rules.tasks || []).map((x) => [x.key, x]));
    const reviewOffer = data.appTasks?.review || null; // the ONE app whose Play Store review is open right now — only during that app's hour
    // Share is a DAILY claim → only today's row counts; the others are once per contest.
    const claimOf = (key) => data.claims.find((c) => c.activity === key && (key !== 'share' || c.claimDate === data.today));
    const sharesDone = data.claims.filter((c) => c.activity === 'share').length;

    // Reward = whatever the API says (claimPoints, from the admin Setting table) — nothing is typed in here.
    // A review whose proof box is open stays listed even after its hour is over: the owner is still writing it in the store.
    const rows = offeredTaskKeys(data, openKey).map((key) => {
        const c = claimOf(key);
        const state = !c ? 'open' : c.status === 'awarded' ? 'done' : c.status === 'rejected' ? 'rejected' : 'pending';
        return { key, ...TASKS[key], c, state, reward: claimPoints?.[key] ?? 0 };
    });
    const doneCount = rows.filter((r) => r.state === 'done').length;
    const leftPoints = rows.filter((r) => r.state === 'open' || r.state === 'pending').reduce((n, r) => n + r.reward, 0);

    // An install is waiting for the owner to log in to the app. Coming back from Google Play / the app, re-read the page
    // data: the backend awards the install the moment that login exists, so the row flips from "waiting" to "added".
    const waitingForInstall = rows.some((r) => r.kind === 'install' && r.state === 'pending');
    useEffect(() => {
        if (!onChanged || !waitingForInstall) return undefined;
        let last = Date.now();
        const refresh = () => {
            if (document.visibilityState !== 'visible' || Date.now() - last < 8000) return;
            last = Date.now();
            onChanged();
        };
        document.addEventListener('visibilitychange', refresh);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', refresh);
            window.removeEventListener('focus', refresh);
        };
    }, [onChanged, waitingForInstall]);

    // The Play Store review is on offer for only ONE hour a day, at a random time for each owner. Re-read the page data the moment that
    // hour begins and the moment it ends, so the row shows up and goes away by itself — no reload. Once today's hour is over, look again
    // after Bangladesh midnight (a new day = a new hour) for a page that stays open for days. All stamps come from the server's clock
    // (serverNow), so a wrong clock on this device cannot shift the moment. A failed re-read is retried every 30 s; a tab that was asleep
    // through the moment re-reads as soon as it is visible again.
    const hourEdge = reviewOffer?.nextChangeAt || data.appTasks?.reviewOpensAt || null; // when the row next appears / disappears today
    const midnightMs = data.phase === 'live' && data.today && reviewTasksOn(data) ? Date.parse(`${data.today}T00:00:00+06:00`) + 86400000 : null;
    const edgeMs = hourEdge ? Date.parse(hourEdge) : midnightMs;
    const isHourEdge = !!hourEdge;
    const serverNow = data.serverNow;
    useEffect(() => {
        if (!onChanged || !edgeMs) return undefined;
        const ahead = Math.max(edgeMs - (serverNow ? Date.parse(serverNow) : Date.now()), 0);
        // the midnight look is spread over a minute so pages that were left open do not all ask at the same second
        let deadline = Date.now() + ahead + 1500 + (isHourEdge ? 0 : Math.random() * 60000);
        let id = null;
        const check = () => {
            const wait = deadline - Date.now();
            if (wait > 0) { id = setTimeout(check, Math.min(wait, 2 ** 31 - 1)); return; }
            deadline = Date.now() + 30000; // new data re-runs this effect; if it never arrives, ask again in 30 s
            id = setTimeout(check, 30000);
            onChanged();
        };
        const onVisible = () => {
            if (document.visibilityState !== 'visible' || Date.now() < deadline) return;
            clearTimeout(id);
            check();
        };
        check();
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearTimeout(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [onChanged, edgeMs, isHourEdge, serverNow]);

    const submitClaim = async (activity) => {
        // Optional proof link: any clean https link goes through (a post, a video, a Google Drive file …); the check is for safety, not for which site it is.
        const typed = isNoProof(activity) ? '' : proof.trim();
        const clean = typed ? sanitizeUrl(typed, { maxLen: MAX_URL }) : '';
        if (typed && !clean) {
            setProofBad(true);
            return;
        }
        const post = activity === 'share' ? CONTEST_SHARE_POSTS.find((p) => p.id === postId) : null;
        setBusyKey(activity);
        try {
            const claim = await contestService.claim({ activity, proofUrl: clean || null, note: post ? `post:${post.id}` : null });
            // An install whose app login already exists comes back awarded — say so instead of "points are added after review".
            notifications.show({ message: t(claim?.status === 'awarded' ? 'contest.earn.pointsAdded' : 'contest.earn.claimedOk'), color: 'green' });
            setOpenKey(null);
            setProof('');
            setProofBad(false);
            setPostId(null);
            if (onChanged) await onChanged();
        } catch (e) {
            notifications.show({ message: e?.code === 'ALREADY_CLAIMED' ? (activity === 'share' ? t('contest.earn.alreadySharedToday') : t('contest.earn.alreadyClaimed')) : (e?.message || t('contest.earn.claimFailed')), color: 'red' });
        } finally {
            setBusyKey(null);
        }
    };

    // What the main button does for one task.
    const onTap = ({ key, kind, app, url }) => {
        if (kind === 'install') {
            // Like a follow: the same tap opens Google Play and sends the claim.
            openLink(app.storeUrl);
            submitClaim(key);
            return;
        }
        if (kind === 'ext' && openKey !== key) openLink(url); // the extension is installed from the store page; the proof box opens beside it
        if (kind === 'review' && openKey !== key) openLink(app.storeUrl); // the review is written in the store; the proof box opens beside it
        if (kind !== 'review' && NO_PROOF.has(key)) {
            if (OPEN_ON_CLAIM[key]) openLink(OPEN_ON_CLAIM[key]);
            submitClaim(key);
            return;
        }
        setOpenKey(openKey === key ? null : key); setProof(''); setProofBad(false); setPostId(null);
    };

    // The daily quiz (contest points for answering questions about the platform) is its own row — it is not a claim, so it is not
    // counted in "done / points left" above; ContestQuizRow draws nothing when there is no quiz on offer for this owner.
    const quizOn = !!data.quiz?.enabled && !data.quiz.finished;
    if (rows.length === 0 && !quizOn) return null;

    return (
        <section className={`${CARD} flex flex-col gap-3.5 p-[22px]`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{t('contest.earn.claimTitle')}</div>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">{fmt(doneCount)}/{fmt(rows.length)}</span>{' '}
                    {t('contest.earn.tasksDone')}
                    {leftPoints > 0 && <> · <span className="font-semibold text-emerald-700 dark:text-emerald-400">+{fmt(leftPoints)}</span> {t('contest.earn.pointsLeft')}</>}
                </div>
            </div>

            <ul className="flex flex-col gap-2">
                <ContestQuizRow quiz={data.quiz} onChanged={onChanged} />
                {rows.map((row) => {
                    const { key, Icon, tone, kind, app, c, state, reward } = row;
                    const isShare = key === 'share';
                    const daily = dailyKeys.includes(key);
                    const done = state === 'done';
                    const title = (isBn ? taskInfo[key]?.titleBn : taskInfo[key]?.title)
                        || (kind
                            ? t(kind === 'install' ? 'contest.earn.act.install' : 'contest.earn.act.app_review', { app: t(`contest.earn.apps.${app.key}`) })
                            : t(`contest.earn.act.${key}`));
                    return (
                        <li key={key} className={`rounded-[14px] border border-zinc-100 dark:border-zinc-800 ${done ? 'bg-zinc-50 dark:bg-zinc-800/40' : 'bg-white dark:bg-zinc-900'}`}>
                            <div className="flex flex-wrap items-center gap-3.5 py-3 pl-3.5 pr-3">
                                <span className={`flex h-10 w-10 flex-none items-center justify-center rounded-[11px] ${tone}`}>
                                    <Icon size={18} />
                                </span>
                                <div className="flex min-w-0 flex-[1_1_200px] flex-col">
                                    {/* Title, then "- " and how often it can be claimed, on the same line (+ the how-to video, when the task has one).
                                        The "- badge" group wraps as one unit when the title is long. */}
                                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <span className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</span>
                                        <span className="inline-flex items-center gap-2">
                                            <span className="text-[15px] text-zinc-400 dark:text-zinc-500" aria-hidden="true">-</span>
                                            <span className={daily ? BADGE_DAILY : BADGE_ONCE}>{t(daily ? 'contest.earn.badgeDaily' : 'contest.earn.badgeOnce')}</span>
                                            <VideoHelpButton url={CONTEST_TASK_VIDEOS[key]} title={title} />
                                        </span>
                                    </span>
                                    {isShare && sharesDone > 0 && (
                                        <span className="mt-1 text-[13px] text-zinc-500 dark:text-zinc-400">{t('contest.earn.sharesDone', { n: fmt(sharesDone) })}</span>
                                    )}
                                    {/* The share only counts when the post carries the contest hashtag */}
                                    {isShare && (
                                        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] text-zinc-500 dark:text-zinc-400">
                                            {t('contest.earn.shareHashtagRule')}
                                            <span className="rounded-full bg-primary/10 px-2 py-px text-xs font-semibold text-primary">{SHARE_HASHTAG}</span>
                                        </span>
                                    )}
                                    {/* Install: what makes the points land — shown while the claim is open and while it waits for the login */}
                                    {kind === 'install' && (state === 'open' || state === 'pending') && (
                                        <span className="mt-1 text-[13px] text-zinc-500 dark:text-zinc-400">{t('contest.earn.installHint')}</span>
                                    )}
                                </div>

                                <div className="ml-auto flex flex-none items-center gap-2.5">
                                    <span className={`font-baloo text-base font-bold ${done || state === 'rejected' ? 'text-zinc-400 dark:text-zinc-500' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                        +{fmt(done ? c.points : reward)}
                                    </span>
                                    {state === 'open' && (
                                        <button
                                            type="button"
                                            disabled={busyKey !== null}
                                            onClick={() => onTap(row)}
                                            className="inline-flex h-[38px] min-w-[104px] items-center justify-center gap-1.5 rounded-[10px] bg-primary px-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
                                        >
                                            {busyKey === key && <IconLoader2 size={14} className="animate-spin" />}
                                            {t(kind === 'install' || kind === 'ext' ? 'contest.earn.install' : kind === 'review' ? 'contest.earn.writeReview' : 'contest.earn.claim')}
                                        </button>
                                    )}
                                    {state === 'pending' && (
                                        <span className={`${CHIP} bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300`}><IconClockHour4 size={14} />{t('contest.earn.waiting')}</span>
                                    )}
                                    {state === 'done' && (
                                        <span className={`${CHIP} bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300`}><IconCheck size={14} stroke={2.4} />{t(isShare ? 'contest.earn.takenToday' : 'contest.earn.added')}</span>
                                    )}
                                    {state === 'rejected' && (
                                        <span className={`${CHIP} bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400`}><IconX size={14} />{t('contest.earn.rejected')}</span>
                                    )}
                                </div>
                            </div>

                            {/* Today's share: pick a post to share on Facebook (list from config), then claim */}
                            {isShare && state === 'open' && CONTEST_SHARE_POSTS.length > 0 && (
                                <div className="space-y-1.5 px-3.5 pb-3">
                                    {CONTEST_SHARE_POSTS.map((p) => (
                                        <div key={p.id} className="flex items-center gap-2 rounded-lg bg-zinc-50 px-2.5 py-2 dark:bg-zinc-800/60">
                                            <span className="min-w-0 flex-1 truncate text-xs text-zinc-700 dark:text-zinc-200">{isBn && p.titleBn ? p.titleBn : p.title}</span>
                                            <a href={fbShareUrl(p.url)} target="_blank" rel="noopener noreferrer" onClick={() => setPostId(p.id)} className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg bg-doc-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-doc-blue-700">
                                                <IconBrandFacebook size={13} />{t('contest.earn.shareOnFb')}
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Optional proof link, then submit */}
                            {openKey === key && state === 'open' && (
                                <div className="px-3.5 pb-3.5">
                                    <div className="flex items-center gap-2">
                                        <input
                                            value={proof}
                                            maxLength={MAX_URL}
                                            onChange={(e) => { setProof(e.target.value); if (proofBad) setProofBad(false); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && busyKey === null) submitClaim(key); }}
                                            placeholder={t(`contest.earn.proofHint.${kind === 'review' ? 'app_review' : key}`)}
                                            inputMode="url"
                                            autoComplete="off"
                                            autoCapitalize="none"
                                            spellCheck={false}
                                            aria-invalid={proofBad}
                                            className={`${INPUT} ${proofBad ? 'border-red-400 focus:ring-red-400/40 dark:border-red-500' : ''}`}
                                        />
                                        <button type="button" disabled={busyKey !== null} onClick={() => submitClaim(key)} className="inline-flex h-11 flex-shrink-0 items-center gap-1.5 rounded-xl bg-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60">
                                            {busyKey === key && <IconLoader2 size={14} className="animate-spin" />}{t('contest.earn.submit')}
                                        </button>
                                    </div>
                                    {proofBad && <div className="mt-1.5 text-xs text-red-500">{t('contest.earn.badLink')}</div>}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            <div className="text-[13px] text-zinc-400 dark:text-zinc-500">{t('contest.earn.manualNote')}</div>
        </section>
    );
}
